import { forwardRef, type ReactNode, type Ref, type ElementType, type HTMLAttributes } from 'react';
import type { BaseProps } from '../../theme/index.js';
import type { TypographyRole } from '@repletoryreach/sdk-utils/sizes';
import { resolveColor } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps, resolveAs } from '../_internal/factory.js';
import './Text.css';

export interface TextProps extends BaseProps, Omit<HTMLAttributes<HTMLElement>, 'color'> {
  as?: ElementType;
  children?: ReactNode;
  /** Typography role from sdk-utils: drives font-size/weight/line-height. */
  role?: TypographyRole;
  /** Truncate to N lines (sets `-webkit-line-clamp`). */
  lineClamp?: number;
  /** Inline text; render as <span>. */
  inline?: boolean;
  /** Inherit font from parent (overrides role token). */
  inherit?: boolean;
  fw?: number | 'thin' | 'extralight' | 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black';
  fz?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number;
  ta?: 'left' | 'center' | 'right' | 'justify' | 'start' | 'end';
  c?: string;
  truncate?: boolean | 'end' | 'start';
  span?: boolean;
}

function fwResolve(v: TextProps['fw']): number | string | undefined {
  if (v === undefined) return undefined;
  if (typeof v === 'number') return v;
  return `var(--rr-fw-${v})`;
}

function fzResolve(v: TextProps['fz']): string | undefined {
  if (v === undefined) return undefined;
  if (typeof v === 'number') return `${v}px`;
  return `var(--rr-fs-${v}, var(--rr-fs-body))`;
}

/** Themed text element bound to a typography role. */
export const Text = forwardRef(function Text(
  { as, role = 'body', lineClamp, inline, inherit, fw, fz, ta, c, truncate, span, children, ...rest }: TextProps,
  ref: Ref<HTMLElement>
): ReactNode {
  const Tag = resolveAs(as, span || inline ? 'span' : 'p');
  const base = useBaseAttrs('rr-text', rest, [
    `rr-text--${role}`,
    inherit && 'rr-text--inherit',
    truncate && 'rr-text--truncate',
    truncate === 'start' && 'rr-text--truncate-start',
    lineClamp !== undefined && lineClamp > 0 && 'rr-text--clamp'
  ]);
  const { dom } = splitBaseProps(rest);

  return (
    <Tag {...dom}
      ref={ref}
      {...base.attrs}
      className={base.className}
      style={{
        ...base.style,
        fontWeight: fwResolve(fw),
        fontSize: fzResolve(fz),
        textAlign: ta,
        color: resolveColor(c) ?? c,
        ...(lineClamp ? { ['--rr-line-clamp' as string]: String(lineClamp) } : null)
      }}
    >
      {children}
    </Tag>
  );
});
